import React, { useLayoutEffect, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import {
  type RouteProp,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import { noop, theme, useResponsive } from '@sg/garnish';

import { useRedeemGiftCardDefaultValues } from '@order/features/gift-card';
import { StripeCustomerSheet } from '@order/integrations/stripe';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useA11ySelector } from '@order/utils';

import {
  AccountPaymentGiftCardLoading,
  AccountScreenContainer,
  AccountScreenTitle,
  GiftCardAccountSection,
  PaymentAccountSection,
  SignedOutView,
} from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import { usePaymentMethods } from '../../hooks';
import type { AccountTabStackParamList } from '../../navigation/AppNavigation.props';

// ─────────────────────────────────────────────────────────────────────────────

export const PaymentAndGiftCardsScreen = () => {
  const { formatMessage } = useIntl();
  const { match } = useResponsive();
  const { paymentMethods, isLoadingPaymentMethods } = usePaymentMethods();

  const title = formatMessage(messages.title);
  const containerA11ySelector = useA11ySelector('account.payments.container');
  const paymentMethodContainerA11ySelector = useA11ySelector('account.payments.payment-method-container'); // prettier-ignore

  const { params } =
    useRoute<RouteProp<AccountTabStackParamList, 'PaymentAndGiftCards'>>();
  const giftCardRedemptionEncodedData = params?.giftCardId;

  const defaultRedemptionValues = useRedeemGiftCardDefaultValues({
    giftCardRedemptionEncodedData,
  });

  // ─── Derived Data ────────────────────────────────────────────────────

  const redirectToParams = useMemo(
    () => ({ redirectToGiftCardId: params?.giftCardId }),
    [params?.giftCardId],
  );

  // ─── Flags ───────────────────────────────────────────────────────────

  const isLoggedIn = useIsLoggedIn();
  const isInStoreOrdersEnabled = useFeatureFlag('CELS-4174-in-store-loyalty-orders-enabled'); // prettier-ignore
  const isStripeSdkEnabled = useFeatureFlag('CELS-4677-account-stripe-sdk-enabled'); // prettier-ignore

  // ─────────────────────────────────────────────────────────────────────

  const navigation = useNavigation();

  useLayoutEffect(() => {
    navigation.setOptions({ headerShown: true });
  }, [navigation]);

  if (!isLoggedIn) {
    return (
      <SignedOutView
        redirectTo="payment-and-gift-cards"
        redirectToParams={redirectToParams}
        withoutFooter
      />
    );
  }

  if (isStripeSdkEnabled) {
    return (
      <AccountScreenContainer
        screen="payment-and-gift-cards"
        {...containerA11ySelector.props}
      >
        <AccountScreenTitle title={title} />

        <StripeCustomerSheet visible onClose={noop} />
      </AccountScreenContainer>
    );
  }

  if (isLoadingPaymentMethods) {
    return <AccountPaymentGiftCardLoading />;
  }

  return (
    <AccountScreenContainer
      screen="payment-and-gift-cards"
      {...containerA11ySelector.props}
    >
      <AccountScreenTitle visibility="COMPACT" title={title} />

      <View style={match([styles.paymentSectionContainerXS, undefined])}>
        <PaymentAccountSection
          paymentMethods={paymentMethods}
          {...paymentMethodContainerA11ySelector.props}
        />
      </View>

      {isInStoreOrdersEnabled ? null : (
        <GiftCardAccountSection
          giftCardCode={defaultRedemptionValues?.giftCardCode}
          giftCardRegistrationCode={
            defaultRedemptionValues?.giftCardRegistrationCode
          }
        />
      )}
    </AccountScreenContainer>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Payments',
    description: 'Payments Screen | Title',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  paymentSectionContainerXS: {
    gap: theme.spacing['4'],
    marginBottom: theme.spacing['10'],
  },
});
